import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import filter from 'lodash/filter'
import classnames from 'classnames'
import hasElementOrSomeParentTheClass from '@Utils/HasElementOrSomeParentTheClass'
import Overlay from '@SiteComponents/Overlay'
import WorkspaceViewHeader from './WorkspaceViewHeader'
import WorkspaceUsersTable from './WorkspaceUsersTable'
import Close from '@Images/languages_view_close.svg'
import Expand from '@Images/languages_view_expand.svg'
import Next from '@Images/languages_view_next.svg'
import Previous from '@Images/languages_view_previous.svg'

import {
  IWorkspace
} from '@SiteModules/Workspace/types'

import {
  getWorkspaces,
  getSaving,
  getHasRenamedWorkspaceSuccessfully,
} from '@SiteModules/Workspace/selectors'

import {
  getDeleteWorkspacePopupIsOpen,
} from '@SiteModules/Popup/selectors'

import {
  getAllWorkspaceUsers,
} from '@SiteModules/WorkspaceUser/selectors'

import {
  renameWorkspace,
} from '@SiteModules/Workspace/actions'

import {
  openDeleteWorkspacePopup,
} from '@SiteModules/Popup/actions'

import './WorkspaceView.scss'

interface WorkspaceViewProps {
  readonly workspace: IWorkspace | undefined
  readonly fullWorkspaceView: boolean
  readonly shouldRender: boolean
  resetOpenedWorkspace(): void
  setOpenedWorkspaceId(workspaceId: number): void
  setFullWorkspaceView(newFullWorkspaceView: boolean): void
}

const WorkspaceView: React.FC<WorkspaceViewProps> = props => {
  const dispatch = useDispatch()

  const {
    fullWorkspaceView,
    workspace,
    shouldRender,
  } = props

  const workspaces = useSelector(getWorkspaces)
  const saving = useSelector(getSaving)
  const hasRenamedWorkspaceSuccessfully = useSelector(getHasRenamedWorkspaceSuccessfully)
  const deleteWorkspacePopupIsOpen = useSelector(getDeleteWorkspacePopupIsOpen)
  const allWorkspaceUsers = useSelector(getAllWorkspaceUsers)

  const halfWorkspaceView = !fullWorkspaceView && shouldRender
  const shouldRenderOverlay = halfWorkspaceView
  const relevantWorkspaceUsers = workspace ? filter(allWorkspaceUsers, workspaceUser => workspaceUser.attributes.workspaceId === workspace.id) : []

  const workspacesRef = useRef(workspaces)
  const workspaceRef = useRef(workspace)
  const deleteWorkspacePopupIsOpenRef = useRef(deleteWorkspacePopupIsOpen)
  const indexOfOpenedWorkspace = workspace ? workspaces.indexOf(workspace) : -1
  const indexOfOpenedWorkspaceRef = useRef(indexOfOpenedWorkspace)
  const isFirstWorkspace = indexOfOpenedWorkspace === 0
  const isLastWorkspace = indexOfOpenedWorkspace === workspaces.length - 1
  const hasWorkspaceUsers = relevantWorkspaceUsers.length > 0

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fullWorkspaceView || deleteWorkspacePopupIsOpenRef.current) { return }

      if (e.target) {
        const element = e.target as HTMLElement
        if (hasElementOrSomeParentTheClass(element, 'WorkspaceView__element')) {
          return
        }
        props.resetOpenedWorkspace()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    indexOfOpenedWorkspaceRef.current = indexOfOpenedWorkspace
  }, [indexOfOpenedWorkspace])

  useEffect(() => {
    workspacesRef.current = workspaces
  }, [workspaces])

  useEffect(() => {
    workspaceRef.current = workspace
  }, [workspace])

  useEffect(() => {
    deleteWorkspacePopupIsOpenRef.current = deleteWorkspacePopupIsOpen
  }, [deleteWorkspacePopupIsOpen])

  const isArrowUp = (event: KeyboardEvent) => {
    return event.key === 'ArrowUp'
  }

  const isArrowDown = (event: KeyboardEvent) => {
    return event.key === 'ArrowDown'
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isArrowUp(event)) {
      event.preventDefault()
      setPreviousWorkspace()
    }

    if (isArrowDown(event)) {
      event.preventDefault()
      setNextWorkspace()
    }
  }

  const setNextWorkspace = () => {
    if (indexOfOpenedWorkspaceRef.current < workspacesRef.current.length - 1 && indexOfOpenedWorkspaceRef.current > -1) {
      props.setOpenedWorkspaceId(workspacesRef.current[indexOfOpenedWorkspaceRef.current + 1].id)
    }
  }

  const setPreviousWorkspace = () => {
    if (indexOfOpenedWorkspaceRef.current > 0) {
      props.setOpenedWorkspaceId(workspacesRef.current[indexOfOpenedWorkspaceRef.current - 1].id)
    }
  }

  const handleRenameWorkspace = (name: string) => {
    if (workspaceRef.current) {
      dispatch(renameWorkspace({ workspaceId: workspaceRef.current.id, name }))
    }
  }

  const handleOpenDeleteWorkspacePopup = () => {
    if (workspaceRef.current) {
      dispatch(openDeleteWorkspacePopup({ workspaceIds: [workspaceRef.current.id] }))
    }
  }

  const renderActionsHalfView = () => {
    return (
      <div className="WorkspaceView__actions">
        <Close
          className="WorkspaceView__actions-close"
          onClick={props.resetOpenedWorkspace}
        />
        <Expand
          className="WorkspaceView__actions-expand"
          onClick={props.setFullWorkspaceView}
        />
        <div className="WorkspaceView__actions-separator"/>
        <Next
          className={classnames(
            'WorkspaceView__actions-next',
            { 'WorkspaceView__actions-next-disabled': isLastWorkspace }
          )}
          onClick={setNextWorkspace}
        />
          <Previous
            className={classnames(
              'WorkspaceView__actions-next',
              { 'WorkspaceView__actions-previous-disabled': isFirstWorkspace }
            )}
            onClick={setPreviousWorkspace}
          />
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <WorkspaceViewHeader
        workspace={workspace}
        numberOfWorkspaceUsers={relevantWorkspaceUsers.length}
        fullWorkspaceView={fullWorkspaceView}
        resetOpenedWorkspace={props.resetOpenedWorkspace}
        handleRenameWorkspace={handleRenameWorkspace}
        saving={saving}
        hasRenamedWorkspaceSuccessfully={hasRenamedWorkspaceSuccessfully}
        openDeleteWorkspacePopup={handleOpenDeleteWorkspacePopup}
      />
    )
  }

  const renderWorkspaceUsersTable = () => {
    return (
      <WorkspaceUsersTable
        workspaceUsers={relevantWorkspaceUsers}
        fullWorkspaceView={fullWorkspaceView}
      />
    )
  }

  return (
    <React.Fragment>
      {shouldRenderOverlay && (
        <Overlay />
      )}
      <div className={classnames(
        'WorkspaceView',
        'WorkspaceView__element',
        { 'WorkspaceView__hidden': !shouldRender },
        { 'WorkspaceView__full': fullWorkspaceView }
      )}>
        {halfWorkspaceView && renderActionsHalfView()}
        {renderHeader()}
        {hasWorkspaceUsers && renderWorkspaceUsersTable()}
      </div>
    </React.Fragment>
  )
}

export default WorkspaceView
